import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
  IconButton,
} from '@mui/material';
import { organizationApi, skillsApi } from '../../services/api';
import { useAlert } from '../../contexts/AlertContext';
import LoadingSpinner from '../common/LoadingSpinner';
import { REVIEW_TYPES, getReviewTypeText } from '../../constants/reviewTypes';
import { COLORS } from '../../constants/radar';
import {
  getTemplateTypeConstant,
  getTemplateTypeText,
  getTemplateTypeColor
} from '../../constants/matrixTemplateTypes';
import { 
  ASSIGNMENT_STATUSES,
  getStatusText,
  getStatusColor,
  getStatusTextColor,
  getStatusBorderColor,
  getReviewerStatus,
  getAssignmentStatusText,
  getAssignmentStatusColor,
  getAssignmentStatusTextColor,
  getAssignmentStatusBorderColor
} from '../../constants/assignmentStatuses';
import { EmployeeAvatar } from '../common/EmployeeAvatar';
import GroupsIcon from '@mui/icons-material/Groups';
import GroupIcon from '@mui/icons-material/Group';
import CloseIcon from '@mui/icons-material/Close';
import { getRelativeTime } from '../../utils/dateUtils';
import BusinessIcon from '@mui/icons-material/Business';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EmptyState from '../common/EmptyState';
import ReviewerAssignmentDialog from './common/ReviewerAssignmentDialog';

const AssignReviewer = ({ 
  businessUnit, 
  employeeAssignments,
  onReviewerAssignment,
  reviewType,
  readOnly,
  forceAsEmployee=false,
  selectedProjects,
  selectedSessionTemplate,
  isIndividualMode = false
}) => {
  
  const { showAlert } = useAlert();
  const [isLoading, setIsLoading] = useState(false);
  const [hierarchyData, setHierarchyData] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [employeeToReview, setEmployeeToReview] = useState(null);
  // The resolved team for the assignee currently in the dialog (enables the
  // "Assign Team Members" shortcut). Selection state now lives in the dialog.
  const [dialogTeam, setDialogTeam] = useState(null);
  const [templatesData, setTemplatesData] = useState([]);

  // Helper function to determine the status of a reviewer assignment
  const getReviewerAssignmentStatus = (reviewerAssignment) => {
    if (!reviewerAssignment?.reviewer?.assignment) return ASSIGNMENT_STATUSES.NOT_STARTED;
    
    const assignment = reviewerAssignment.reviewer.assignment;
    
    // Create a temporary object with just the dates to pass to getReviewerStatus
    const reviewerObj = {
      completedAt: assignment.completedAt,
      startedAt: assignment.startedAt
    };
    
    // Use the existing getReviewerStatus function
    return getReviewerStatus(reviewerObj);
  };

  // Helper function to get the review timing text
  const getReviewTiming = (reviewerAssignment) => {
    if (!reviewerAssignment?.reviewer?.assignment) return null;
    
    const assignment = reviewerAssignment.reviewer.assignment;
    
    if (assignment.completedAt) {
      // For completed reviews, show time since completion
      return `Completed ${getRelativeTime(assignment.completedAt)}`;
    } else if (assignment.startedAt) {
      // For ongoing reviews, show time since start
      return `Started ${getRelativeTime(assignment.startedAt)}`;
    }
    
    // For not started reviews, don't show timing
    return null;
  };

  useEffect(() => {
    if (businessUnit?.id) {
      fetchHierarchyData();
    } else if (isIndividualMode) {
      // In individual mode without a business unit, create empty hierarchy
      setHierarchyData({
        id: 'empty',
        name: 'Individual Assignments',
        projects: [],
        childBus: []
      });
    }
  }, [businessUnit, isIndividualMode]);

  // Updated to use the new API endpoint to fetch templates by session ID
  useEffect(() => {
    if (selectedSessionTemplate?.id) {
      const fetchTemplatesData = async () => {
        try {
          setIsLoading(true);
          const templates = await skillsApi.getSkillsMatrixTemplatesBySessionId(selectedSessionTemplate.id);
          setTemplatesData(templates);
        } catch (error) {
          showAlert('Failed to fetch templates data', 'error', error);
          setTemplatesData([]);
        } finally {
          setIsLoading(false);
        }
      };

      fetchTemplatesData();
    } else {
      setTemplatesData([]);
    }
  }, [selectedSessionTemplate]);

  const fetchHierarchyData = async () => {
    try {
      setIsLoading(true);
      const data = await organizationApi.getBusinessUnitDetailedHierarchy(businessUnit.id, forceAsEmployee);
      
      // Filter the hierarchy data based on selected projects
      if (selectedProjects && selectedProjects.size > 0) {
        // Create a filtered copy of the hierarchy with only selected projects
        const filteredData = filterHierarchyBySelectedProjects(data, selectedProjects);
        setHierarchyData(filteredData);
      } else {
        // If no projects are selected, remove all projects from the hierarchy
        const filteredData = filterHierarchyBySelectedProjects(data, new Set());
        setHierarchyData(filteredData);
      }
    } catch {
      showAlert('Failed to fetch team structure', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to filter hierarchy and keep only selected projects
  const filterHierarchyBySelectedProjects = (data, selectedProjects) => {
    // Do a proper deep clone of the relevant parts of the data
    const filteredData = {
      ...data,
      // Filter projects at the top level - empty array if no projects selected
      projects: selectedProjects.size > 0 && data.projects ? 
        data.projects
          .filter(project => selectedProjects.has(project.id))
          .map(project => ({...project})) 
        : [],
      // Process child business units
      childBus: data.childBus ? 
        data.childBus.map(childBu => filterBusinessUnit(childBu, selectedProjects))
        : []
    };
    
    return filteredData;
  };
  
  // Helper function to recursively filter a business unit
  const filterBusinessUnit = (businessUnit, selectedProjects) => {
    return {
      ...businessUnit,
      // Filter projects in this business unit - empty array if no projects selected
      projects: selectedProjects.size > 0 && businessUnit.projects ? 
        businessUnit.projects
          .filter(project => selectedProjects.has(project.id))
          .map(project => ({...project})) 
        : [],
      // Process child business units recursively
      childBus: businessUnit.childBus ? 
        businessUnit.childBus.map(childBu => filterBusinessUnit(childBu, selectedProjects))
        : []
    };
  };

  // EmployeeSearch component now handles fetching available reviewers

  // Find the assignee's team within the fetched BU hierarchy, so the dialog can
  // offer the "Assign Team Members" shortcut.
  const findEmployeeTeam = (data, employeeId) => {
    const searchTeams = (teams) => {
      if (!teams) return null;
      for (const team of teams) {
        if (team.employees?.some(emp => emp.id === employeeId)) {
          return team;
        }
        if (team.childTeams) {
          const found = searchTeams(team.childTeams);
          if (found) return found;
        }
      }
      return null;
    };
    for (const project of data?.projects || []) {
      const found = searchTeams(project.teams);
      if (found) return found;
    }
    return null;
  };

  const handleOpenDialog = (employee) => {
    setEmployeeToReview(employee);
    setDialogTeam(findEmployeeTeam(hierarchyData, employee.id));
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEmployeeToReview(null);
    setDialogTeam(null);
    setIsDialogOpen(false);
  };

  // Single reviewer chosen in the dialog — append to the assignee's local list.
  const handleConfirm = ({ reviewer, template }) => {
    if (!reviewer || !template || !employeeToReview) return;
    const assignment = employeeAssignments.get(employeeToReview.id);
    if (!assignment) return;

    const reviewerAssignment = { reviewer, template };

    // If review type is Sequential, add reviewType and order
    if (reviewType === REVIEW_TYPES.SEQUENTIAL) {
      reviewerAssignment.reviewType = REVIEW_TYPES.SEQUENTIAL;

      // Find the maximum order among existing reviewers
      const maxOrder = assignment.reviewers
        .filter(r => r.reviewType === REVIEW_TYPES.SEQUENTIAL)
        .reduce((max, r) => Math.max(max, r.order || 0), 0);

      // Set order to max + 1
      reviewerAssignment.order = maxOrder + 1;
    }

    // Get current reviewers and add new one
    const updatedReviewers = [...assignment.reviewers, reviewerAssignment];

    onReviewerAssignment(employeeToReview.id, updatedReviewers);
    handleCloseDialog();
  };

  // Bulk team list built by the dialog — replace the assignee's reviewers.
  const handleAssignTeam = (reviewerAssignments) => {
    if (!employeeToReview || !reviewerAssignments) return;
    onReviewerAssignment(employeeToReview.id, reviewerAssignments);
    handleCloseDialog();
  };

  const handleRemoveReviewer = (employeeId, reviewerId) => {
    const assignment = employeeAssignments.get(employeeId);
    if (!assignment) return;

    // Find the reviewer index by reviewerId
    const reviewerIndex = assignment.reviewers.findIndex(r => r.reviewer.id === reviewerId);
    if (reviewerIndex === -1) return; // Reviewer not found
    
    const updatedReviewers = [...assignment.reviewers];
    const removedReviewer = updatedReviewers[reviewerIndex];
    
    // Remove the reviewer
    updatedReviewers.splice(reviewerIndex, 1);
    
    // If this is a sequential review and the removed reviewer had an order,
    // adjust the order of the remaining reviewers
    if (reviewType === REVIEW_TYPES.SEQUENTIAL && removedReviewer.reviewType === REVIEW_TYPES.SEQUENTIAL) {
      const removedOrder = removedReviewer.order || 0;
      
      // Adjust the order of reviewers that came after the removed one
      updatedReviewers.forEach(reviewer => {
        if (reviewer.reviewType === REVIEW_TYPES.SEQUENTIAL && reviewer.order > removedOrder) {
          reviewer.order -= 1;
        }
      });
    }
    
    onReviewerAssignment(employeeId, updatedReviewers);
  };

  const renderEmployeeCard = (employee) => {
    const assignment = employeeAssignments.get(employee.id);
    if (!assignment) return null;
    
    // Skip employees marked as deleted
    if (assignment.deleted === true) return null;

    const employeeAssignmentObj = {
      ...assignment.assignment,
      reviewers: assignment.reviewers.map(reviewer => reviewer.reviewer.assignment)
    }

    return (
      <Box key={employee.id} sx={{ display: 'flex', mb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            p: 1,
            backgroundColor: '#FFFFFF',
            borderRadius: 1,
            border: '1px solid #E2E8F0',
            cursor: readOnly ? 'default' : 'pointer',
            width: '280px',
            height: '90px',
            position: 'relative',
            '&:hover': {
              backgroundColor: readOnly ? '#FFFFFF' : '#F8FAFC',
              boxShadow: readOnly ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
              transform: readOnly ? 'none' : 'translateY(-1px)',
              borderColor: readOnly ? '#E2E8F0' : COLORS.PRIMARY,
              transition: 'all 0.2s ease'
            }
          }}
          onClick={() => !readOnly && handleOpenDialog(employee)}
        >
          <EmployeeAvatar
            employee={employee}
            size={28}
            sx={{ mr: 1 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: COLORS.PRIMARY,
                  fontWeight: 'normal',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                  fontSize: '13px',
                  lineHeight: 1.2,
                  mb: 0.25
                }}
              >
                {employee.fullName}
              </Typography>
              {readOnly ? (
                <Chip
                  label={getAssignmentStatusText(employeeAssignmentObj)}
                  size="small"
                  sx={{
                    height: '18px',
                    fontSize: '10px',
                    backgroundColor: getAssignmentStatusColor(employeeAssignmentObj),
                    color: getAssignmentStatusTextColor(employeeAssignmentObj),
                    borderColor: getAssignmentStatusBorderColor(employeeAssignmentObj),
                    borderWidth: 1,
                    fontWeight: 500,
                    '& .MuiChip-label': {
                      px: 1
                    }
                  }}
                />
              ) : (
                <Chip
                  icon={<PersonAddIcon sx={{ fontSize: '12px !important', ml: '4px' }} />}
                  label="Add Reviewer"
                  size="small"
                  sx={{
                    height: '18px',
                    fontSize: '10px',
                    backgroundColor: '#F0F7FF',
                    color: COLORS.PRIMARY,
                    border: `1px solid ${COLORS.PRIMARY}`,
                    fontWeight: 500,
                    '& .MuiChip-label': {
                      pr: 1,
                      pl: 0.5
                    },
                    '& .MuiChip-icon': {
                      color: COLORS.PRIMARY,
                      marginRight: '4px'
                    }
                  }}
                />
              )}
            </Box>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#64748B',
                fontStyle: 'italic',
                display: 'flex',
                alignItems: 'center',
                gap: '2px',
                fontSize: '10px',
                lineHeight: 1.2,
                mb: 0.25
              }}
            >
              {employee.position?.label}
              {employee.grade && (
                <>
                  <Box component="span" sx={{ mx: 0.25 }}>•</Box>
                  {employee.grade.label}
                </>
              )}
            </Typography>
            {assignment.matrix && (
              <Typography 
                variant="caption" 
                sx={{ 
                      color: '#64748B',
                      fontSize: '10px',
                      display: 'block',
                      mt: 1
                }}
              >
                Assigned to {assignment.matrix.name}
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          ml: 1,
          alignContent: 'flex-start',
          flex: 1
        }}>
          {[...assignment.reviewers]
            // Sort reviewers by order for Sequential reviews, keeping original order for others
            .sort((a, b) => {
              // If both have reviewType and it's SEQUENTIAL, sort by order
              if (a.reviewType === REVIEW_TYPES.SEQUENTIAL && b.reviewType === REVIEW_TYPES.SEQUENTIAL) {
                return (a.order || 0) - (b.order || 0);
              }
              // If only a is SEQUENTIAL, it comes first
              if (a.reviewType === REVIEW_TYPES.SEQUENTIAL) return -1;
              // If only b is SEQUENTIAL, it comes first
              if (b.reviewType === REVIEW_TYPES.SEQUENTIAL) return 1;
              // Otherwise keep original order
              return 0;
            })
            .map((reviewerAssignment, index) => (
            <Box
              key={`${reviewerAssignment.reviewer.id}-${index}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                p: 1,
                backgroundColor: '#F8FAFC',
                borderRadius: 1,
                border: '1px solid #E2E8F0',
                width: '280px',
                height: '90px',
                position: 'relative',
                '&:hover .remove-button': {
                  opacity: 1
                }
              }}
            >
              <EmployeeAvatar
                employee={reviewerAssignment.reviewer}
                size={28}
                sx={{ mr: 1 }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      color: '#64748B',
                      fontWeight: 'normal',
                      fontSize: '13px',
                      lineHeight: 1.2,
                      mb: 0.25
                    }}
                  >
                    {reviewerAssignment.reviewer.fullName}
                  </Typography>
                  {/* Add reviewer status chip - only visible in read-only mode */}
                  {readOnly && (
                    <>
                      <Chip
                        label={getStatusText(getReviewerAssignmentStatus(reviewerAssignment))}
                        size="small"
                        sx={{
                          height: '18px',
                          fontSize: '10px',
                          backgroundColor: getStatusColor(getReviewerAssignmentStatus(reviewerAssignment)),
                          color: getStatusTextColor(getReviewerAssignmentStatus(reviewerAssignment)),
                          borderColor: getStatusBorderColor(getReviewerAssignmentStatus(reviewerAssignment)),
                          borderWidth: 1,
                          fontWeight: 500,
                          '& .MuiChip-label': {
                            px: 1
                          }
                        }}
                      />
                    </>
                  )}
                </Box>
                {reviewerAssignment.template && (
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: getTemplateTypeColor(getTemplateTypeConstant(reviewerAssignment.template.type)),
                      fontSize: '11px',
                      lineHeight: 1.2,
                      mb: 0.25
                    }}
                  >
                    {getTemplateTypeText(getTemplateTypeConstant(reviewerAssignment.template.type))}
                  </Typography>
                )}
                
                {/* Add timing information if available and in read-only mode */}
                {readOnly && getReviewTiming(reviewerAssignment) && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#64748B',
                      fontStyle: 'italic',
                      fontSize: '10px',
                      display: 'block',
                      mb: 0.25
                    }}
                  >
                    {getReviewTiming(reviewerAssignment)}
                  </Typography>
                )}
                
                {/* Add review type if available */}
                {reviewerAssignment.reviewType && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: '#64748B',
                      fontSize: '10px',
                      display: 'block',
                      mt: 0.5
                    }}
                  >
                    {getReviewTypeText(reviewerAssignment.reviewType)}
                    {reviewerAssignment.order && ` • Order: ${reviewerAssignment.order}`}
                  </Typography>
                )}
              </Box>
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveReviewer(employee.id, reviewerAssignment.reviewer.id);
                }}
                sx={{ 
                  position: 'absolute', 
                  top: 8, 
                  right: 8,
                  display: readOnly
                    || (reviewType === REVIEW_TYPES.SEQUENTIAL && reviewerAssignment.order === 1)
                    || (reviewType === REVIEW_TYPES.PARALLEL && reviewerAssignment.reviewer.id === employee.id)
                    ? 'none' : 'flex'
                }}
                className="remove-button"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const renderTeam = (team, depth = 0) => (
    <Box key={team.id} sx={{ mb: 1, ml: depth * 2 }}>
      <Typography variant="subtitle1" sx={{ 
        mb: 0.5, 
        display: 'flex', 
        alignItems: 'center',
        fontSize: depth === 0 ? '14px' : '14px',
        color: depth === 0 ? COLORS.PRIMARY : '#64748B',
        fontWeight: depth === 0 ? '600' : 'normal',
        lineHeight: 1.2
      }}>
        {depth === 0 ? <GroupIcon sx={{
          mr: 1,
          fontSize: 20,
          color: COLORS.PRIMARY
        }} /> : <GroupsIcon sx={{
          mr: 1, 
          fontSize: 18,
          color: '#94A3B8'
        }} />}
        {team.name}
        {team.manager && (
          <>
            <Typography variant="body2" sx={{ mx: 0.5, color: '#64748B' }}>•</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <EmployeeAvatar
                employee={team.manager}
                sx={{ 
                  mr: 0.25,
                  width: '16px',
                  height: '16px',
                  '& .MuiAvatar-root': {
                    width: '16px',
                    height: '16px',
                    fontSize: '12px'
                  }
                }}
              />
              <Typography variant="body2" sx={{ 
                color: '#64748B',
                fontSize: '12px',
                lineHeight: 1.2
              }}>
                {team.manager.fullName}
              </Typography>
            </Box>
          </>
        )}
      </Typography>
      <Stack spacing={0.5}>
        {team.employees?.filter(employee => {
          const assignment = employeeAssignments.get(employee.id);
          // Only include employees that have assignments and are not deleted
          return assignment && assignment.deleted !== true;
        })
          .map(employee => renderEmployeeCard(employee))}
      </Stack>
      {team.childTeams?.map(childTeam => renderTeam(childTeam, depth + 1))}
    </Box>
  );

  // Extract all business unit owners from the current business unit and its child business units
  const extractBusinessUnitOwners = (bu) => {
    const owners = [];
    const processedOwnerIds = new Set(); // Track processed owner IDs to avoid duplicates
    
    // Helper function to process a business unit and extract its owner
    const processBusinessUnit = (businessUnit) => {
      if (businessUnit.owner && !processedOwnerIds.has(businessUnit.owner.id)) {
        // Add owner ID to processed set
        processedOwnerIds.add(businessUnit.owner.id);
        
        // Ensure owner has all necessary properties for rendering
        owners.push({
          ...businessUnit.owner,
          id: businessUnit.owner.id,
          fullName: businessUnit.owner.fullName || businessUnit.owner.name || 'Unknown',
          businessUnitName: businessUnit.name,
          isBusinessUnitOwner: true,
          // Add default values for properties that might be missing but are expected by renderEmployeeCard
          position: businessUnit.owner.position || { label: 'Business Unit Owner' },
          grade: businessUnit.owner.grade || null,
          azureId: businessUnit.owner.azureId || null,
          engagementType: businessUnit.owner.engagementType || null,
          roles: businessUnit.owner.roles || [{ label: 'Business Unit Owner' }]
        });
      }
      
      // Recursively process child business units
      if (businessUnit.childBus && businessUnit.childBus.length > 0) {
        businessUnit.childBus.forEach(childBu => processBusinessUnit(childBu));
      }
    };
    
    // Start processing from the root business unit
    processBusinessUnit(bu);
    
    return owners;
  };

  // Render business unit owners section
  const renderBusinessUnitOwners = (owners) => {
    if (!owners || owners.length === 0) return null;
    
    // Filter out deleted owners
    const filteredOwners = owners.filter(owner => {
      const assignment = employeeAssignments.get(owner.id);
      return assignment && assignment.deleted !== true;
    });
    
    if (filteredOwners.length === 0) return null;
    
    return (
      <Box sx={{ mb: 4  }}>
        <Typography 
          sx={{ 
            fontWeight: 600, 
            color: COLORS.PRIMARY,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px'
          }}
        >
          <BusinessIcon sx={{ mr: 1, fontSize: 18 }} />
          Business Unit Owners
        </Typography>
        <Box sx={{ ml: 1 }}>
          <Stack spacing={1}>
            {filteredOwners.map(owner => (
              <Box 
                key={owner.id}
                sx={{
                  p: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${COLORS.BACKGROUND_DARK}`,
                  backgroundColor: COLORS.BACKGROUND_LIGHT,
                  mb: 1,
                  maxWidth: '280px'
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: COLORS.PRIMARY, fontStyle: 'italic', fontSize: '12px' }}>
                        Owner of {owner.businessUnitName}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Stack>
          <Stack spacing={0.5} sx={{ mt: 2 }}>
            {filteredOwners.map(owner => renderEmployeeCard(owner))}
          </Stack>
        </Box>
      </Box>
    );
  };

  const renderIndividualAssignments = () => {
    const assignedEmployees = Array.from(employeeAssignments.entries())
      .filter(([, assignment]) => !assignment.deleted)
      .map(([employeeId, assignment]) => ({
        id: employeeId,
        ...assignment.employee,
        assignment: assignment
      }));

    return (
      <Box sx={{ mt: 2 }}>
        {assignedEmployees.length === 0 ? (
          <EmptyState
            title="No Individuals Assigned"
            description="No individuals have been selected for individual assignments yet."
          />
        ) : (
          <Box>
            <Typography 
              sx={{ 
                fontWeight: 600, 
                color: COLORS.PRIMARY,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                fontSize: '14px'
              }}
            >
              <PersonAddIcon sx={{ mr: 1, fontSize: 18 }} />
              Individual Assignments
            </Typography>
            {assignedEmployees.map(employee => renderEmployeeCard(employee))}
          </Box>
        )}
      </Box>
    );
  };

  const renderHierarchyMatrix = () => {
    if (!hierarchyData) return null;

    // In individual mode, show all assigned employees directly
    if (isIndividualMode) {
      return renderIndividualAssignments();
    }

    // Regular mode logic
    // Get all business unit owners
    const businessUnitOwners = extractBusinessUnitOwners(hierarchyData);
    
    // Filter owners that are in employeeAssignments
    const assignedOwners = businessUnitOwners.filter(owner => 
      employeeAssignments.has(owner.id)
    );

    // Get extra evaluees
    const extraEvaluees = extractExtraEvaluees(hierarchyData, businessUnitOwners);

    return (
      <Box sx={{ mt: 2 }}>
        {/* Render business unit owners at the top */}
        {renderBusinessUnitOwners(assignedOwners)}

        {/* Render extra evaluees after business unit owners */}
        {renderExtraEvaluees(extraEvaluees)}

        {/* Render projects as before */}
        {hierarchyData.projects?.map(project => (
          <Box key={project.id} sx={{ mb: 2 }}>
            {project.teams?.map(team => renderTeam(team))}
            <Divider sx={{ my: 1 }} />
          </Box>
        ))}
      </Box>
    );
  };


  // Extract all employee IDs from the hierarchy data
  const getAllEmployeeIdsFromHierarchy = (hierarchyData) => {
    if (!hierarchyData) return new Set();
    
    const employeeIds = new Set();
    
    // Add the business unit owner if exists
    if (hierarchyData.owner) {
      employeeIds.add(hierarchyData.owner.id);
    }
    
    // Process child business units
    if (hierarchyData.childBus) {
      hierarchyData.childBus.forEach(childBu => {
        // Add business unit owner if exists
        if (childBu.owner) {
          employeeIds.add(childBu.owner.id);
        }
      });
    }
    
    // Process projects
    if (hierarchyData.projects) {
      hierarchyData.projects.forEach(project => {
        // Process teams in the project
        if (project.teams) {
          project.teams.forEach(team => {
            // Process team recursively
            collectEmployeeIdsFromTeam(team, employeeIds);
          });
        }
      });
    }
    
    return employeeIds;
  };
  
  // Helper function to collect employee IDs from a team and its children
  const collectEmployeeIdsFromTeam = (team, employeeIds) => {
    // Add team manager if exists
    /*
    if (team.manager) {
      employeeIds.add(team.manager.id);
    }
    */
    
    // Add team employees
    if (team.employees) {
      team.employees.forEach(employee => {
        employeeIds.add(employee.id);
      });
    }
    
    // Process child teams
    if (team.childTeams) {
      team.childTeams.forEach(childTeam => {
        collectEmployeeIdsFromTeam(childTeam, employeeIds);
      });
    }
  };

  // Extract extra evaluees from employeeAssignments that are not part of the hierarchy or business unit owners
  const extractExtraEvaluees = (hierarchyData, businessUnitOwners) => {
    // Get all employee IDs from the hierarchy
    const hierarchyEmployeeIds = getAllEmployeeIdsFromHierarchy(hierarchyData);
    
    // Add business unit owner IDs to the set
    businessUnitOwners.forEach(owner => {
      hierarchyEmployeeIds.add(owner.id);
    });
    
    // Extract employees from employeeAssignments that are not in the hierarchy
    const extraEvaluees = [];
    
    employeeAssignments.forEach((assignment, employeeId) => {
      // Include employees that:
      // 1. Are not in the hierarchy OR
      // 2. Have the explicit isExtraEvaluee flag set to true
      if (!hierarchyEmployeeIds.has(employeeId) || assignment.isExtraEvaluee === true) {
        // This is an extra evaluee
        extraEvaluees.push({
          id: employeeId,
          fullName: assignment.employee?.fullName || 'Unknown',
          position: assignment.employee?.position,
          grade: assignment.employee?.grade,
          azureId: assignment.employee?.azureId,
          engagementType: assignment.employee?.engagementType,
          roles: assignment.employee?.roles || [],
          isExtraEvaluee: true
        });
      }
    });
    
    return extraEvaluees;
  };

  // Render extra evaluees section
  const renderExtraEvaluees = (extraEvaluees) => {
    if (!extraEvaluees || extraEvaluees.length === 0) return null;
    
    // Filter out deleted evaluees
    const filteredEvaluees = extraEvaluees.filter(evaluee => {
      const assignment = employeeAssignments.get(evaluee.id);
      return assignment && assignment.deleted !== true;
    });
    
    if (filteredEvaluees.length === 0) return null;
    
    return (
      <Box sx={{ mb: 4 }}>
        <Typography 
          sx={{ 
            fontWeight: 600, 
            color: COLORS.PRIMARY,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            fontSize: '14px'
          }}
        >
          <PersonAddIcon sx={{ mr: 1, fontSize: 18 }} />
          Extra Evaluees
        </Typography>
        <Box sx={{ ml: 1 }}>
          <Stack spacing={0.5} sx={{ mt: 2 }}>
            {filteredEvaluees.map(evaluee => renderEmployeeCard(evaluee))}
          </Stack>
        </Box>
      </Box>
    );
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading team structure..." />;
  }

  const dialogAssignment = employeeToReview ? employeeAssignments.get(employeeToReview.id) : null;

  return (
    <Box>
      {renderHierarchyMatrix()}
      <ReviewerAssignmentDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        employeeToReview={employeeToReview}
        existingReviewers={dialogAssignment?.reviewers || []}
        matrix={dialogAssignment?.matrix}
        templates={templatesData}
        reviewType={reviewType}
        team={dialogTeam}
        onConfirm={handleConfirm}
        onAssignTeam={handleAssignTeam}
      />
    </Box>
  );
};

export default AssignReviewer; 